import { SHARED, INCOME_DISCLAIMER } from 'app/models/questions/shared';
import { RadioField } from 'app/components/FormFields/RadioField';
import { PurchaseDownPayment } from 'app/components/FormFields/PurchaseDownPayment';
import { Select } from 'app/components/FormFields/Select';
import { Address } from 'app/components/FormFields/Address';
import { State } from 'app/components/FormFields/State';
import { ZipCode } from 'app/components/FormFields/ZipCode';
import { Currency } from 'app/components/FormFields/Currency';
import {
  YES_OR_NO_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  COBORROWER_EMPLOYMENT_TYPE_OPTIONS,
  REAL_ESTATE_LIABILITY_OPTIONS,
} from 'app/models/options/options';
import { RetirementIncomeSources } from 'app/components/FormFieldArrays/RetirementIncomeSources';
import { IncomeSources } from 'app/components/FormFieldArrays/IncomeSources';
import { OtherIncomeSources } from 'app/components/FormFieldArrays/OtherIncomeSources';
import { RealEstateAccountsStatus } from 'app/components/FormFieldArrays/RealEstateAccountsStatus';
import { LoanOfficerLanding } from 'app/components/LoanOfficerLanding';
import { PrequalAmount } from 'app/components/PrequalAmount';
import { Question } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  coBorrowerHasDifferentAddress,
  isVeteran,
  isEmployee,
  isSelfEmployed,
  isRetired,
  isEmploymentOther,
  isCoBorrowerEmployee,
  isCoBorrowerSelfEmployed,
  isCoBorrowerRetired,
  isCoBorrowerEmploymentOther,
} from 'app/models/fields/conditionals';
import { TextInput } from 'app/components/FormFields/TextInput';

export const autoPrequalWelcomeQuestionByName = (name: string): Question => {
  return AUTO_PREQUAL_WELCOME_QUESTIONS.find((question) => {
    return question.name === name;
  }) as Question;
};

export const autoPrequalWelcomeQuestionsByName = (names: string[] = []): Question[] => {
  const questions: Question[] = [];
  for (const name of names) {
    questions.push(autoPrequalWelcomeQuestionByName(name));
  }
  return questions;
};

export const AUTO_PREQUAL_WELCOME_QUESTIONS: Question[] = [
  {
    id: 2001,
    ...SHARED[FieldNames.firstName],
  },
  {
    id: 2002,
    ...SHARED[FieldNames.middleName],
  },
  {
    id: 2003,
    ...SHARED[FieldNames.lastName],
  },
  {
    id: 2004,
    ...SHARED[FieldNames.email],
  },
  {
    id: 2005,
    ...SHARED[FieldNames.phone],
  },
  {
    id: 2006,
    ...SHARED[FieldNames.dateOfBirth],
  },
  {
    id          : 2009,
    component   : PurchaseDownPayment,
    hideTitle   : true,
    label       : 'Home Value',
    name        : FieldNames.homeValue,
    fieldPopover: true,
    popover     : 'Enter an estimated value here and our calculations will narrow everything down for you.',
  },
  {
    id: 2010,
    ...SHARED[FieldNames.propertyType],
    component   : Select,
    fieldPopover: true,
  },
  {
    id: 2011,
    ...SHARED[FieldNames.propertyUsage],
    component   : Select,
    fieldPopover: true,
  },
  {
    id: 2012,
    ...SHARED[FieldNames.propertyZip],
  },
  {
    id       : 2016,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.livingPropertyStreet,
    title    : 'What is your current address?',
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPropertyStreet,
    },
  },
  {
    id        : 2017,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.livingPropertyStreet2,
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPropertyStreet2,
    },
  },
  {
    id       : 2018,
    component: Address,
    label    : 'City',
    name     : FieldNames.livingPropertyCity,
  },
  {
    id       : 2019,
    component: State,
    label    : 'State',
    name     : FieldNames.livingPropertyState,
  },
  {
    id       : 2020,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.livingPropertyZip,
  },
  {
    id       : 2021,
    ...SHARED[FieldNames.maritalStatus],
  },
  {
    id       : 2022,
    component: RadioField,
    name     : FieldNames.coBorrowerYN,
    options  : YES_OR_NO_OPTIONS,
    title    : 'Will you have a co-borrower?',
  },
  {
    id: 2023,
    ...SHARED[FieldNames.firstName],
    name: FieldNames.coBorrowerFirstName,
  },
  {
    id: 2024,
    ...SHARED[FieldNames.middleName],
    name: FieldNames.coBorrowerMiddleName,
  },
  {
    id: 2025,
    ...SHARED[FieldNames.lastName],
    name: FieldNames.coBorrowerLastName,
  },
  {
    id: 2026,
    ...SHARED[FieldNames.dateOfBirth],
    name: FieldNames.coBorrowerDOB,
  },
  {
    id       : 2027,
    component: RadioField,
    name     : FieldNames.coBorrowerSameAddress,
    title    : 'Same address as borrower?',
    options  : YES_OR_NO_OPTIONS,
  },
  {
    id       : 2028,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.coBorrowerLivingPropertyStreet,
    title    : 'Co-borrower\'s current address?',
    showIf   : [coBorrowerHasDifferentAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.coBorrowerLivingPropertyStreet,
    },
  },
  {
    id        : 2029,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.coBorrowerLivingPropertyStreet2,
    showIf    : [coBorrowerHasDifferentAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.coBorrowerLivingPropertyStreet2,
    },
  },
  {
    id       : 2030,
    component: Address,
    label    : 'City',
    name     : FieldNames.coBorrowerLivingPropertyCity,
    showIf   : [coBorrowerHasDifferentAddress],
  },
  {
    id       : 2031,
    component: State,
    label    : 'State',
    name     : FieldNames.coBorrowerLivingPropertyState,
    showIf   : [coBorrowerHasDifferentAddress],
  },
  {
    id       : 2032,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.coBorrowerLivingPropertyZip,
    showIf   : [coBorrowerHasDifferentAddress],
  },
  {
    id       : 2033,
    component: RadioField,
    title    : 'Are you a veteran or an active-duty service member?',
    name     : FieldNames.veteranYN,
    options  : YES_OR_NO_OPTIONS,
  },
  {
    id       : 2034,
    component: RadioField,
    title    : 'Are you planning to get a VA loan?',
    name     : FieldNames.veteranLoanYN,
    options  : YES_OR_NO_OPTIONS,
    showIf   : [isVeteran],
  },
  {
    id       : 2035,
    component: LoanOfficerLanding,
    name     : FieldNames.loanOfficer,
  },
  {
    id         : 2036,
    component  : RadioField,
    name       : FieldNames.employmentType,
    title      : 'What is your income source?',
    disclaimer : INCOME_DISCLAIMER,
    options    : EMPLOYMENT_TYPE_OPTIONS,
  },
  {
    id       : 2037,
    component: Currency,
    name     : FieldNames.employBase,
    label    : 'Monthly Base Pay',
    showIf   : [isEmployee, isSelfEmployed],
  },
  {
    id          : 2038,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Overtime',
    name        : FieldNames.employOvertime,
    showIf      : [isEmployee, isSelfEmployed],
    popover     :
      'Overtime can be included in your income if it has been consistent over a two-year period.',
    title: 'Additional income sources?',
  },
  {
    id          : 2039,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Commission',
    name        : FieldNames.employCommission,
    showIf      : [isEmployee, isSelfEmployed],
    popover     :
      'Commissions can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id          : 2040,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Bonus',
    name        : FieldNames.employBonus,
    showIf      : [isEmployee, isSelfEmployed],
    popover     :
      'Bonuses can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id          : 2041,
    component   : IncomeSources,
    name        : FieldNames.additionalIncome,
    fieldPopover: true,
    isOptional  : true,
    popover     : 'Income derived from alimony, child support, or separate maintenance payments need not be revealed if the applicant does not want the creditor to consider it in determining the applicant\'s creditworthiness.',
    showIf      : [isEmployee, isSelfEmployed],
  },
  {
    id       : 2042,
    component: RetirementIncomeSources,
    name     : FieldNames.retirementIncome,
    showIf   : [isRetired],
  },
  {
    id       : 2043,
    component: OtherIncomeSources,
    name     : FieldNames.otherIncome,
    showIf   : [isEmploymentOther],
  },
  {
    id       : 2044,
    component: RadioField,
    title    : 'Is the income above consistent and verifiable over the past 2 years?',
    name     : FieldNames.incomeVerifiable,
    options  : YES_OR_NO_OPTIONS,
  },
  {
    id         : 2045,
    component  : RadioField,
    title      : 'What is your co-borrower’s income source?',
    name       : FieldNames.coBorrowerEmploymentType,
    disclaimer : INCOME_DISCLAIMER,
    options    : COBORROWER_EMPLOYMENT_TYPE_OPTIONS,
  },
  {
    id         : 2046,
    component  : Currency,
    name       : FieldNames.coBorrowerEmployBase,
    label      : 'Monthly Base Pay',
    showIf     : [isCoBorrowerEmployee, isCoBorrowerSelfEmployed],
  },
  {
    id          : 2047,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Overtime',
    name        : FieldNames.coBorrowerEmployOvertime,
    showIf      : [isCoBorrowerEmployee, isCoBorrowerSelfEmployed],
    popover     :
      'Overtime can be included in your income if it has been consistent over a two-year period.',
    title: 'Additional income sources?',
  },
  {
    id          : 2048,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Commission',
    name        : FieldNames.coBorrowerEmployCommission,
    showIf      : [isCoBorrowerEmployee, isCoBorrowerSelfEmployed],
    popover     :
      'Commissions can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id          : 2049,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Bonus',
    name        : FieldNames.coBorrowerEmployBonus,
    showIf      : [isCoBorrowerEmployee, isCoBorrowerSelfEmployed],
    popover     :
      'Bonuses can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id          : 2050,
    component   : IncomeSources,
    name        : FieldNames.coBorrowerAdditionalIncome,
    fieldPopover: true,
    popover     : 'Income derived from alimony, child support, or separate maintenance payments need not be revealed if the applicant does not want the creditor to consider it in determining the applicant\'s creditworthiness.',
    showIf      : [isCoBorrowerEmployee, isCoBorrowerSelfEmployed],
  },
  {
    id       : 2051,
    component: RetirementIncomeSources,
    name     : FieldNames.coBorrowerRetirementIncome,
    showIf   : [isCoBorrowerRetired],
  },
  {
    id       : 2052,
    component: OtherIncomeSources,
    name     : FieldNames.coBorrowerOtherIncome,
    showIf   : [isCoBorrowerEmploymentOther],
  },
  {
    id       : 2053,
    component: RadioField,
    title    : 'Is the income above consistent and verifiable over the past 2 years?',
    name     : FieldNames.coBorrowerIncomeVerifiable,
    options  : YES_OR_NO_OPTIONS,
  },
  {
    id       : 2054,
    component: RealEstateAccountsStatus,
    name     : FieldNames.realEstateLiabilities,
    options  : REAL_ESTATE_LIABILITY_OPTIONS,
  },
  {
    id       : 2055,
    component: PrequalAmount,
    name     : FieldNames.prequalAmount,
  },
  {
    id: 2057,
    ...SHARED[FieldNames.suffixName],
  },
  {
    id: 2058,
    ...SHARED[FieldNames.coBorrowerSuffixName],
  },
  {
    id        : 2059,
    component : TextInput,
    label     : 'Employer Name',
    name      : FieldNames.employCompanyName,
    showIf    : [isEmployee, isSelfEmployed],
  },
  {
    id        : 2060,
    component : TextInput,
    label     : 'Employer Name',
    name      : FieldNames.coBorrowerEmployCompanyName,
    showIf    : [isCoBorrowerEmployee, isCoBorrowerSelfEmployed],
  },
];
